"use client";

import { cn } from "@/lib/utils";
import { BookmarkIcon, ExternalLinkIcon, XIcon } from "lucide-react";
import { useEffect, useState } from "react";

import CalcomIcon from "@/public/admin-sidebar/cal.svg";
import FigmaIcon from "@/public/admin-sidebar/figma.svg";
import GoogleAnalyticsIcon from "@/public/admin-sidebar/google-analytics.svg";
import GoogleDriveIcon from "@/public/admin-sidebar/google-drive.svg";
import GoogleSearchConsoleIcon from "@/public/admin-sidebar/google-search-console.svg";
import GoogleTagManagerIcon from "@/public/admin-sidebar/google-tag-manager.svg";
import ResendIcon from "@/public/admin-sidebar/resend.svg";

export function useIsEditing() {
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const isInIframe = window.self !== window.top;
      setIsEditing(isInIframe);
    }
  }, []);

  return isEditing;
}

function ResourceLink({ title, subtitle, icon: Icon, iconClassName = "w-5 h-5", href = "" }) {
  return (
    <a
      className="group border border-gray-100 flex items-center gap-3 px-3 py-3 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
    >
      <div className="w-10 h-10 flex shrink-0 items-center justify-center rounded-md bg-gray-50 group-hover:bg-white transition-colors">
        <Icon className={iconClassName} />
      </div>
      <div className="min-w-0 flex-1 space-y-0.5">
        <div className="font-semibold text-sm text-gray-900">{title}</div>
        <div className="text-gray-500 text-xs truncate">{subtitle}</div>
      </div>
      <ExternalLinkIcon className="w-3.5 h-3.5 text-gray-300 group-hover:text-gray-500 shrink-0 transition-colors" />
    </a>
  );
}

function AdminSidebarInner() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isEditing = useIsEditing();

  if (!isEditing) return null;

  return (
    <>
      <button
        type="button"
        className={cn(
          "fixed top-6 right-0 z-110 flex h-10 w-12 cursor-pointer items-center justify-center rounded-l-md",
          "bg-primary-600 hover:bg-primary-700 transition-colors"
        )}
        onClick={() => setDrawerOpen(!drawerOpen)}
      >
        {drawerOpen ? <XIcon className="ml-1 h-5 w-5 text-white" /> : <BookmarkIcon className="ml-1 h-5 w-5 stroke-[1.5] text-white" />}
      </button>

      <div className={cn("fixed top-0 right-0 z-100 h-screen bg-white shadow-lg border-l border-gray-100")}>
        <div className={cn("relative flex h-screen transform transition-all duration-300 ease-out overflow-hidden", drawerOpen ? "max-w-[400px]" : "max-w-0")}>
          <div className="flex min-w-[400px] flex-col gap-6 overflow-hidden p-6">
            <div className="flex flex-1 flex-col gap-4 overflow-auto">
              <div className="select-none text-lg font-semibold text-gray-900">Bookmarks</div>
              <div className="space-y-2">
                {/* Google Drive */}
                <ResourceLink
                  icon={GoogleDriveIcon}
                  iconClassName="w-6 h-6"
                  title="Google Drive"
                  subtitle="Website repository"
                  href="https://drive.google.com/drive/folders/1yJiaWpCGAThJiTKHZkl_z3WSWPtUtWbI?usp=sharing"
                />
                <ResourceLink
                  icon={FigmaIcon}
                  iconClassName="w-6 h-6"
                  title="Figma Designs"
                  subtitle="View the mockups & designs"
                  href="https://www.figma.com/design/8sAdnlSuGzA38vDfqUzaY1/Sl%C3%A1nDocs?node-id=7208-8360&p=f&t=CedqtTfRhp0MOpu3-0"
                />

                <ResourceLink
                  icon={GoogleTagManagerIcon}
                  iconClassName="w-6 h-6"
                  title="Google Tag Manager"
                  subtitle="Manage tracking tags & marketing pixels"
                  href="https://tagmanager.google.com"
                />
                <ResourceLink
                  icon={GoogleAnalyticsIcon}
                  iconClassName="w-6 h-6"
                  title="Google Analytics"
                  subtitle="Website traffic & analytics"
                  href="https://analytics.google.com"
                />
                <ResourceLink
                  icon={GoogleSearchConsoleIcon}
                  iconClassName="w-6 h-6"
                  title="Google Search Console"
                  subtitle="Search performance & indexing"
                  href="https://search.google.com/search-console"
                />
                <ResourceLink icon={CalcomIcon} iconClassName="w-8 h-8" title="Cal.com" subtitle="Booking calendar" href="https://cal.eu/" />
                <ResourceLink icon={ResendIcon} iconClassName="w-7 h-7" title="Resend" subtitle="Email service" href="https://resend.com" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default function AdminSidebar() {
  return <AdminSidebarInner />;
}
